// Tela do Backoffice do Faturamento na Visão Geral (#6501): Brenda. Abre no dia, sem seletor.
//
// Pergunta da tela: o rateio do mês está certo, e as faturas e as usinas estão em dia?
//   - rateio do mês: a planilha que monta a janela e o prazo da próxima (Rateio Antigo);
//   - titularidades: trocas assinadas que ainda não aparecem na fatura da Equatorial;
//   - faturas que não chegaram: Conferência do Faturamento + fila do robô da Equatorial;
//   - usinas agora e geração do dia: Geradoras (as usinas são da Brenda);
//   - saldo das UCs: os indicadores do painel Saldos das UCs.
// Quem entra e sai do rateio (#6506): aprovadas fora da planilha, canceladas ainda dentro e
// UCs para voltar. Titularidade em 3 dias úteis (#6509): o protocolo da troca na Equatorial é
// registrado na própria lista de titularidades pendentes e vira o cartão do mês.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BatteryCharging, ClipboardCheck, FileInput, Sun, Zap } from 'lucide-react';
import { mesAtualFaturas } from '../../../src/lib/conferenciaFaturamentoDb';
import { mesAnterior } from '../../../src/lib/conferenciaFaturamento';
import { ROTULO_MOTIVO_ROBO, type MotivoRobo } from '../../Financeiro/lib/janelaFaturamentoLista';
import { FAIXA_META } from '../../Gestao/Rateio/saldosUcsVisual';
import {
  META_FATURAS_EMAIL, faturasPeloEmail, faturasQueNaoChegaram, geracaoDoDia, planilhasNoPrazo, roboParado, situacaoDoRateio, usinasAgora,
  type EstadoPrazoPlanilha, type GeracaoDaUsina, type UsinaAgora,
} from '../../../src/lib/visaoGeral/backoffice';
import type { StatusDesvio } from '../../Geradoras/geradorasSeries';
import { BlocosDoDia } from '../BlocosDoDia';
import { BotaoAcao, CabecalhoVisaoGeral, CartaoHoje, CartaoIndicador, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral, plural, type Tom } from '../componentes';
import { useConferenciaDoMes } from '../useGestaoFinanceiro';
import {
  useFilaDoRobo, useGeracaoDoDia, useInterrupcoesAbertas, usePlanilhasRateio, useSaldosDasUcs, useTitularidades,
  useUcsComFaturaPorEmail, useUsinasBase,
} from '../useBackoffice';
import { PainelEntraESai, useEntraESai } from '../blocosFase2';
import { CartaoTitularidadeNoPrazo, ModalProtocoloTitularidade } from '../blocosFase3';
import { useProtocolosTitularidade } from '../useRegistrosFase3';
import { rotuloDoMes } from '../../../src/lib/dashboard/metasMensais';
import { isoLocal } from '../../../src/lib/visaoGeral/periodo';
import { prazoDoProtocolo } from '../../../src/lib/visaoGeral/registrosFase3';
import type { TrocaAcompanhada } from '../../../src/lib/visaoGeral/backoffice';
import type { PropsTelaVisaoGeral } from '../tipos';

const LISTA = 8;
const pct = (v: number, casas = 0) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: casas })}%`;
const dataCurta = (iso: string | null | undefined) => {
  if (!iso) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const dataHora = (iso: string) => {
  const d = new Date(iso);
  return `${dataCurta(iso)} ${String(d.getHours()).padStart(2, '0')}h`;
};
const fmtKwh = (v: number | null) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1000) return `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MWh`;
  return `${Math.round(v).toLocaleString('pt-BR')} kWh`;
};
/** 'AAAA-MM' do mês de referência "OUT/2026". */
const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const mesIso = (mes: string) => `${mes.slice(4)}-${String(MESES.indexOf(mes.slice(0, 3)) + 1).padStart(2, '0')}`;
const mesDoIso = (iso: string | null) => (iso ? `${MESES[Number(iso.slice(5, 7)) - 1]}/${iso.slice(0, 4)}` : '');

const COR_DESVIO: Record<StatusDesvio, string> = {
  acima: 'text-emerald-600 dark:text-emerald-400',
  esperado: 'text-gray-700 dark:text-gray-200',
  abaixo: 'text-amber-700 dark:text-amber-400',
  critico: 'text-red-600 dark:text-red-400',
  sem_referencia: 'text-gray-400',
};

const ROTULO_PRAZO: Record<EstadoPrazoPlanilha, string> = {
  no_prazo: 'no prazo',
  atrasada: 'atrasada',
  so_copia: 'só a cópia de outro mês',
  nao_subiu: 'não subiu',
};

/** O que a usina tem agora, em uma linha. */
function situacaoDaUsina(u: UsinaAgora): { texto: string; tom: Tom } {
  const partes: string[] = [];
  const sr = u.semReportar;
  if (sr) {
    if (sr.falha === 'nunca_reportou') partes.push('Nunca reportou');
    else if (sr.falha === 'sem_leitura') partes.push(`Sem reportar há ${Math.round(sr.horas_paradas ?? 0)} h`);
    else partes.push(sr.ultima_geracao ? `Sem gerar desde ${dataCurta(sr.ultima_geracao)}` : 'Nunca gerou');
  }
  const it = u.interrupcao;
  if (it && !(sr && it.tipo === 'parada')) {
    if (it.tipo === 'parada') partes.push(`Parada desde ${dataHora(it.inicio)}${it.a_confirmar ? ' (a confirmar)' : ''}`);
    else if (it.tipo === 'parcial') partes.push(`Gerando ${Math.round(it.pct_da_referencia ?? 0)}% das irmãs desde ${dataCurta(it.inicio)}`);
    else if (it.tipo === 'sem_comunicacao') partes.push(`Sem comunicação desde ${dataHora(it.inicio)}`);
    else partes.push(`Coletor sem leitura desde ${dataHora(it.inicio)}`);
  }
  if (partes.length === 0) return { texto: u.alarmes ? `Operando · ${plural(u.alarmes, 'alarme', 'alarmes')} em 72 h` : 'Operando', tom: 'ok' };
  return { texto: partes.join(' · '), tom: sr || it?.tipo === 'parada' ? 'critico' : 'atencao' };
}

const TOM_TEXTO: Record<Tom, string> = {
  critico: 'text-red-600 dark:text-red-400',
  atencao: 'text-amber-700 dark:text-amber-400',
  ok: 'text-emerald-700 dark:text-emerald-400',
  neutro: 'text-gray-600 dark:text-gray-300',
};

/** Geração contra a esperada, na cor do desvio. */
const parte = (real: number | null | undefined, esperado: number | null | undefined, status: StatusDesvio | undefined) => (
  esperado && real != null
    ? <span className={`font-semibold ${COR_DESVIO[status ?? 'sem_referencia']}`}>{pct(real / esperado)}</span>
    : <span className="text-gray-400">—</span>
);

const TelaBackoffice: React.FC<PropsTelaVisaoGeral> = ({ pessoa, souEu, definicao, periodo }) => {
  const navigate = useNavigate();
  const mesAtual = mesAtualFaturas();
  const mesPassado = mesAnterior(mesAtual) ?? mesAtual;
  const mesesEmail = useMemo(() => [mesIso(mesAtual), mesIso(mesPassado)], [mesAtual, mesPassado]);

  // ── dados ──
  const planilhas = usePlanilhasRateio();
  const conferencia = useConferenciaDoMes(mesAtual);
  const conferenciaPassada = useConferenciaDoMes(mesPassado);
  const fila = useFilaDoRobo(mesAtual);
  const email = useUcsComFaturaPorEmail(mesesEmail);
  const titularidades = useTitularidades();
  // Protocolo da troca na Equatorial (#6509): lido à parte para a lista não depender da migration.
  const [versaoProtocolo, setVersaoProtocolo] = useState(0);
  const protocolos = useProtocolosTitularidade(versaoProtocolo);
  const [trocaDoProtocolo, setTrocaDoProtocolo] = useState<TrocaAcompanhada | null>(null);
  const calendario = useMemo(() => {
    const h = new Date();
    return { inicio: new Date(h.getFullYear(), h.getMonth(), 1), fim: new Date(h.getFullYear(), h.getMonth() + 1, 1), hojeIso: isoLocal(h), rotulo: rotuloDoMes(isoLocal(h).slice(0, 7)) };
  }, []);
  const usinasBase = useUsinasBase();
  const interrupcoes = useInterrupcoesAbertas();
  const sites = useMemo(() => (usinasBase.dados ? [...new Set(usinasBase.dados.unidades.map(u => u.site_id_api))] : null), [usinasBase.dados]);
  const geracao = useGeracaoDoDia(sites);
  const saldos = useSaldosDasUcs();
  const entraSai = useEntraESai();

  // ── contas ──
  const rateio = useMemo(() => (planilhas.dados ? situacaoDoRateio(planilhas.dados) : null), [planilhas.dados]);
  const prazos = useMemo(() => (planilhas.dados ? planilhasNoPrazo(planilhas.dados) : null), [planilhas.dados]);
  const naoChegaram = useMemo(
    () => (conferencia.carga && fila.dados ? faturasQueNaoChegaram(conferencia.carga.resultado.linhas, fila.dados, mesAtual) : null),
    [conferencia.carga, fila.dados, mesAtual],
  );
  const robo = naoChegaram
    ? { parado: roboParado(naoChegaram), ultima: naoChegaram.ultimaTentativaRobo }
    : null;
  const semFaturaPassado = conferenciaPassada.carga?.resultado.linhas.filter(l => l.detalhe === 'fatura_nao_recebida').length ?? null;
  const emailAtual = useMemo(
    () => (conferencia.carga && email.dados ? faturasPeloEmail(conferencia.carga.resultado.linhas, email.dados.get(mesIso(mesAtual)) ?? new Set()) : null),
    [conferencia.carga, email.dados, mesAtual],
  );
  const emailPassado = useMemo(
    () => (conferenciaPassada.carga && email.dados ? faturasPeloEmail(conferenciaPassada.carga.resultado.linhas, email.dados.get(mesIso(mesPassado)) ?? new Set()) : null),
    [conferenciaPassada.carga, email.dados, mesPassado],
  );
  const usinas = useMemo(
    () => (usinasBase.dados ? usinasAgora({ ...usinasBase.dados, interrupcoes: interrupcoes.dados ?? [] }) : null),
    [usinasBase.dados, interrupcoes.dados],
  );
  const gerado = useMemo(
    () => (usinasBase.dados && geracao.dados ? geracaoDoDia(usinasBase.dados.unidades, geracao.dados.hoje, geracao.dados.ontem) : null),
    [usinasBase.dados, geracao.dados],
  );
  const geracaoPorSite = useMemo(() => new Map<string, GeracaoDaUsina>((gerado?.usinas ?? []).map(g => [g.site, g])), [gerado]);
  const tit = titularidades.dados;
  const noNomeAntigo = tit?.pendentes.filter(p => p.estado === 'fatura_no_nome_antigo').length ?? 0;

  // ── cartão do rateio ──
  const cartaoRateio = (() => {
    if (!rateio) return { valor: null as string | null, rotulo: 'planilha do rateio', tom: 'neutro' as Tom, dica: undefined as string | undefined };
    const proxima = rateio.proxima.jaSubiu
      ? `A de ${rateio.proxima.mes} já subiu.`
      : `A de ${rateio.proxima.mes} vence em ${dataCurta(rateio.proxima.prazo)} (${rateio.proxima.diasParaPrazo >= 0 ? `faltam ${plural(rateio.proxima.diasParaPrazo, 'dia', 'dias')}` : `passou há ${plural(-rateio.proxima.diasParaPrazo, 'dia', 'dias')}`}).`;
    const u = rateio.usada;
    switch (rateio.estado) {
      case 'em_dia':
        return { valor: rateio.planilhaEsperada, rotulo: `planilha que monta a janela de ${rateio.mesFaturas}`, tom: 'ok' as Tom, dica: `Subiu em ${dataCurta(u?.carregadaEm)}${u?.por ? ` por ${u.por}` : ''}. ${proxima}` };
      case 'copia':
        return {
          valor: rateio.planilhaEsperada,
          rotulo: `é cópia de outro mês e monta a janela de ${rateio.mesFaturas}`,
          tom: 'atencao' as Tom,
          dica: `A planilha real de ${rateio.planilhaEsperada} não subiu: a janela usa uma carga feita em ${dataCurta(u?.carregadaEm)}${u?.por ? ` (${u.por})` : ''}. UC que entrou no rateio depois dela fica de fora. ${proxima}`,
        };
      case 'aguardando':
        return { valor: rateio.planilhaEsperada, rotulo: `planilha vence em ${plural(rateio.diasParaPrazo, 'dia', 'dias')}`, tom: 'atencao' as Tom, dica: `Prazo: ${dataCurta(rateio.prazo)}, antes das primeiras faturas de ${rateio.mesFaturas}. Até lá a janela repete a de ${u?.mes ?? '—'}.` };
      default:
        return { valor: rateio.planilhaEsperada, rotulo: 'planilha do rateio não subiu', tom: 'critico' as Tom, dica: `O prazo era ${dataCurta(rateio.prazo)}. A janela de ${rateio.mesFaturas} está repetindo a de ${u?.mes ?? '—'}: UC que entrou no rateio depois dela fica de fora.` };
    }
  })();

  const motivosDaFalha = naoChegaram
    ? (Object.entries(naoChegaram.porMotivo) as [MotivoRobo, number][]).sort((a, b) => b[1] - a[1])
    : [];

  const acoes = souEu ? <BotaoAcao rotulo="Rateio Antigo" icone={Zap} para="/gestao/rateio-antigo" /> : null;

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} acoes={acoes} />

      <BlocosDoDia pessoa={pessoa} perfil="backoffice" />

      <SecaoVisaoGeral titulo="Hoje" subtitulo="rateio, titularidades, faturas e usinas">
        <CartaoHoje valor={cartaoRateio.valor} rotulo={cartaoRateio.rotulo} tom={cartaoRateio.tom} para="/gestao/rateio-antigo" dica={cartaoRateio.dica} />
        <CartaoHoje
          valor={entraSai.r ? entraSai.r.aprovadasFora.length + entraSai.r.canceladasDentro.length : null}
          rotulo="para entrar ou sair do rateio"
          tom={entraSai.r && entraSai.r.aprovadasFora.some(a => a.dias > 30) ? 'critico' : 'atencao'}
          para="/gestao/rateio-antigo"
          dica={entraSai.r ? `${plural(entraSai.r.aprovadasFora.length, 'negociação aprovada', 'negociações aprovadas')} sem UC na planilha de ${entraSai.planilha} e ${plural(entraSai.r.canceladasDentro.length, 'cancelamento concluído', 'cancelamentos concluídos')} com a UC ainda recebendo energia.` : undefined}
        />
        <CartaoHoje
          valor={tit ? tit.pendentes.length : null}
          rotulo="titularidades assinadas sem a fatura no nome do cliente"
          tom="atencao"
          dica={tit ? `Clientes com o contrato assinado: ${plural(noNomeAntigo, 'já teve', 'já tiveram')} fatura do mês seguinte ainda no nome antigo; ${tit.pendentes.length - noNomeAntigo} sem fatura nova desde a assinatura. Mais ${plural(tit.aguardandoAssinatura, 'documento esperando', 'documentos esperando')} a assinatura do cliente. Fora da conta: ${plural(tit.semTermoAssinado, 'troca', 'trocas')} de termo reprovado ou ainda não assinado.` : undefined}
        />
        <CartaoHoje
          valor={naoChegaram ? naoChegaram.atrasadas : null}
          rotulo={`faturas de ${mesAtual} atrasadas, sem chegar`}
          tom={naoChegaram && naoChegaram.atrasadas > 0 ? 'critico' : 'ok'}
          para="/financeiro/conferencia-faturamento"
          dica={naoChegaram ? `Das ${naoChegaram.total} UCs do rateio ainda sem a fatura de ${mesAtual}, ${naoChegaram.atrasadas} já passaram do prazo de chegada (17 dias depois da leitura); as outras ainda estão no prazo. Robô da Equatorial: ${naoChegaram.naFila} na fila, ${naoChegaram.falhou} com falha, ${naoChegaram.foraDaFila} fora da fila${robo?.parado ? `; parado, sem tentar desde ${robo.ultima ? dataHora(robo.ultima) : 'o começo do mês'}` : ''}.` : undefined}
        />
        <CartaoHoje
          valor={usinas ? usinas.comProblema : null}
          rotulo="usinas paradas, sem gerar ou com interrupção"
          tom={usinas && usinas.comProblema > 0 ? 'critico' : 'ok'}
          para="/geradoras"
          dica={usinas ? `${usinas.semReportar} sem reportar ou sem gerar (mesma regra do aviso de usina parada) e ${usinas.interrompidas} com interrupção em aberto${interrupcoes.dados ? '' : ' (a detecção ainda está carregando)'}. ${plural(usinas.comAlarme, 'usina', 'usinas')} com alarme nas últimas 72 h.` : undefined}
        />
      </SecaoVisaoGeral>
      {(conferencia.erro || fila.erro || planilhas.erro) && (
        <p className="-mt-6 text-xs text-red-600 dark:text-red-400">{conferencia.erro || 'Não foi possível carregar parte dos dados do rateio e do robô.'}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PainelDetalhe titulo="Faturas que não chegaram" acao={{ rotulo: 'Conferência', para: '/financeiro/conferencia-faturamento' }}>
          {naoChegaram === null ? (
            <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : naoChegaram.total === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Todas as UCs do rateio já têm a fatura de {mesAtual}.</p>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                <strong className={naoChegaram.atrasadas ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}>{naoChegaram.atrasadas}</strong> atrasadas ·{' '}
                robô: {naoChegaram.naFila} na fila, {naoChegaram.falhou} com falha, {naoChegaram.foraDaFila} fora da fila
                {robo?.ultima && !robo.parado ? ` · última tentativa ${dataHora(robo.ultima)}` : ''}
              </p>
              {robo?.parado && (
                <p className="text-sm font-semibold text-red-600 dark:text-red-400 mb-2">
                  Robô da Equatorial parado: {naoChegaram.naFila} UCs na fila e nenhuma tentativa desde {robo.ultima ? dataHora(robo.ultima) : 'o começo do mês'}. Confira o computador do robô.
                </p>
              )}
              {motivosDaFalha.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {motivosDaFalha.map(([m, n]) => (
                    <span key={m} className={`text-xs rounded-full px-2 py-0.5 ${m === 'dados_incorretos' ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {ROTULO_MOTIVO_ROBO[m]}: {n}
                    </span>
                  ))}
                </div>
              )}
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {naoChegaram.lista.slice(0, LISTA).map(f => (
                  <li key={f.linha.chave} className="flex items-start gap-3 py-2.5">
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{f.linha.cliente}</span>
                      <span className="block text-xs text-gray-400">
                        UC {f.linha.uc} · {f.linha.chegadaPrevista ? `${f.linha.chegadaAtrasada ? 'devia ter chegado' : 'chega por volta de'} ${dataCurta(f.linha.chegadaPrevista)}` : 'sem previsão de chegada'}
                      </span>
                    </span>
                    <span className={`shrink-0 text-right text-xs font-semibold ${f.motivo === 'dados_incorretos' ? 'text-red-600 dark:text-red-400' : f.robo === 'falhou' ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {f.robo === 'na_fila' ? 'Na fila do robô' : f.robo === 'falhou' ? ROTULO_MOTIVO_ROBO[f.motivo ?? 'outro'] : 'Fora da fila do robô'}
                      {f.tentativaEm && f.robo === 'falhou' && <span className="block font-normal text-gray-400">tentou em {dataCurta(f.tentativaEm)}</span>}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 mt-3">
                UCs do rateio sem fatura de {mesAtual} no sistema, atrasadas primeiro. "Dados do titular incorretos" pede correção do CPF ou da data de nascimento na UC antes de o robô tentar de novo.
                {semFaturaPassado ? ` De ${mesPassado}, ${plural(semFaturaPassado, 'UC ainda está', 'UCs ainda estão')} sem fatura.` : ''}
                {naoChegaram.total > LISTA && ` Mais ${plural(naoChegaram.total - LISTA, 'fatura', 'faturas')} na Conferência.`}
              </p>
            </>
          )}
        </PainelDetalhe>

        <PainelDetalhe titulo="Titularidades pendentes">
          {titularidades.erro ? (
            <p className="text-sm text-red-600 dark:text-red-400">Não foi possível carregar as trocas de titularidade.</p>
          ) : !tit ? (
            <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : tit.pendentes.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma troca assinada esperando a fatura no nome do cliente.</p>
          ) : (
            <>
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {tit.pendentes.slice(0, LISTA).map(p => {
                  const prot = protocolos.dados?.porTermo.get(p.troca.id) ?? null;
                  const prazo = p.troca.assinado_em ? prazoDoProtocolo(p.troca.assinado_em) : null;
                  const atrasado = !prot?.titularidade_protocolada_em && !!prazo && calendario.hojeIso > prazo;
                  return (
                  <li key={p.troca.id}>
                    <button type="button" onClick={() => navigate(`/crm?negociacaoId=${p.troca.negociacao_id}`)}
                      className="w-full flex items-start gap-3 pt-2.5 pb-1 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2">
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{p.troca.cliente_nome || p.negociacao?.titulo || 'Sem nome'}</span>
                        <span className="block text-xs text-gray-400">
                          UC {p.troca.uc_numero || '—'} · assinada em {dataCurta(p.troca.assinado_em)}
                          {p.negociacao?.status === 'termo_reprovado' ? ' · termo reprovado' : ''}
                        </span>
                      </span>
                      <span className={`shrink-0 text-right text-xs font-semibold ${p.estado === 'fatura_no_nome_antigo' ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                        há {plural(p.diasDesdeAssinatura ?? 0, 'dia', 'dias')}
                        <span className="block font-normal text-gray-500 dark:text-gray-400">
                          {p.estado === 'fatura_no_nome_antigo' ? `fatura de ${mesDoIso(p.ultimaFatura?.mes_referencia ?? null)} no nome antigo` : 'sem fatura nova'}
                        </span>
                      </span>
                    </button>
                    {protocolos.dados && !protocolos.dados.semMigration && (
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pb-2.5 text-xs">
                        {prot?.titularidade_protocolada_em ? (
                          <span className="text-emerald-700 dark:text-emerald-400">
                            Protocolo {prot.protocolo_titularidade} em {dataCurta(prot.titularidade_protocolada_em)}
                            {prot.saldo_nao_se_aplica ? ' · sem saldo' : prot.saldo_protocolado_em ? ` · saldo em ${dataCurta(prot.saldo_protocolado_em)}` : ' · saldo sem protocolo'}
                          </span>
                        ) : (
                          <span className={atrasado ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}>
                            Sem protocolo na Equatorial{prazo ? ` · prazo ${dataCurta(prazo)}` : ''}
                          </span>
                        )}
                        <button type="button" onClick={() => setTrocaDoProtocolo(p)} className="font-semibold text-primary hover:underline">
                          {prot?.titularidade_protocolada_em ? 'Editar' : 'Registrar protocolo'}
                        </button>
                      </div>
                    )}
                  </li>
                  );
                })}
              </ul>
              <p className="text-xs text-gray-400 mt-3">
                Só clientes com o contrato assinado. A troca termina quando a fatura da Equatorial sai com o CPF ou CNPJ do cliente ({plural(tit.efetivadas, 'troca já terminou', 'trocas já terminaram')} assim).
                "Sem fatura nova": nenhuma fatura de mês seguinte à assinatura chegou ainda (quase sempre, cliente que ainda não entrou no rateio). Registre o protocolo da troca na Equatorial (meta: até 3 dias úteis depois da assinatura).
                {tit.pendentes.length > LISTA && ` Mais ${plural(tit.pendentes.length - LISTA, 'troca', 'trocas')}.`}
              </p>
            </>
          )}
          {protocolos.dados?.semMigration && (
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">O registro do protocolo na Equatorial ainda não está disponível (falta aplicar a atualização do banco).</p>
          )}
          {trocaDoProtocolo && (
            <ModalProtocoloTitularidade
              troca={trocaDoProtocolo}
              protocolo={protocolos.dados?.porTermo.get(trocaDoProtocolo.troca.id) ?? null}
              aoFechar={() => setTrocaDoProtocolo(null)}
              aoSalvar={() => { setTrocaDoProtocolo(null); setVersaoProtocolo(v => v + 1); }}
            />
          )}
        </PainelDetalhe>
      </div>

      <PainelEntraESai e={entraSai} reincluir={saldos.dados ? saldos.dados.indicadores.reincluir : null} />

      <PainelDetalhe titulo="Usinas agora" acao={{ rotulo: 'Geradoras', para: '/geradoras' }}>
        {!usinas ? (
          <div className="h-40 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
        ) : (
          <>
            <div className="hidden sm:block overflow-x-auto -mx-2">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="font-medium py-2 px-2">Usina</th>
                    <th className="font-medium py-2 px-2">Agora</th>
                    <th className="font-medium py-2 px-2 text-right">Hoje até agora</th>
                    <th className="font-medium py-2 px-2 text-right">Ontem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {usinas.usinas.map(u => {
                    const s = situacaoDaUsina(u);
                    const g = geracaoPorSite.get(u.site);
                    return (
                      <tr key={u.site}>
                        <td className="py-2 px-2 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{u.nome}</td>
                        <td className={`py-2 px-2 text-xs ${TOM_TEXTO[s.tom]}`}>{s.texto}</td>
                        <td className="py-2 px-2 text-right whitespace-nowrap">
                          {g ? <>{parte(g.hoje, g.esperadoHoje, g.statusHoje)} <span className="text-xs text-gray-400">· {fmtKwh(g.hoje)}</span></> : <span className="text-gray-400">…</span>}
                        </td>
                        <td className="py-2 px-2 text-right whitespace-nowrap">
                          {g ? parte(g.ontem, g.esperadoOntem, g.statusOntem) : <span className="text-gray-400">…</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Celular: uma linha por usina, sem tabela larga. */}
            <ul className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {usinas.usinas.map(u => {
                const s = situacaoDaUsina(u);
                const g = geracaoPorSite.get(u.site);
                return (
                  <li key={u.site} className="py-2.5">
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{u.nome}</span>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        hoje {g ? parte(g.hoje, g.esperadoHoje, g.statusHoje) : '…'} · ontem {g ? parte(g.ontem, g.esperadoOntem, g.statusOntem) : '…'}
                      </span>
                    </span>
                    <span className={`block text-xs mt-0.5 ${TOM_TEXTO[s.tom]}`}>{s.texto}</span>
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-gray-400 mt-3">
              Percentual = geração da usina contra a esperada, que é o que ela geraria rendendo, por kWp, como a mediana das outras usinas no mesmo período (régua "irmãs" da aba Análise).
              Interrupção é a detecção da aba Análise (últimos 3 dias); "a confirmar" quer dizer que ainda não chegou dado para saber se a usina gerou.
              Alarme sozinho não acende a usina: a Chint não avisa quando o alarme termina.
            </p>
          </>
        )}
      </PainelDetalhe>

      <SecaoVisaoGeral titulo="Meu mês" subtitulo={`${mesAtual}, faturas, titularidade, rateio e usinas`}>
        <CartaoIndicador
          titulo={`Faturas pelo e-mail em ${mesPassado}`}
          valor={emailPassado ? (emailPassado.taxa == null ? '—' : pct(emailPassado.taxa)) : null}
          meta={emailPassado?.taxa != null ? { texto: `meta ${pct(META_FATURAS_EMAIL)}`, atingido: emailPassado.taxa / META_FATURAS_EMAIL } : undefined}
          detalhe={emailPassado ? `${emailPassado.pelosEmail} de ${emailPassado.recebidas} UCs do rateio receberam a fatura pelo e-mail da Vigor.${emailAtual?.taxa != null ? ` ${mesAtual} até agora: ${pct(emailAtual.taxa)} (${emailAtual.pelosEmail} de ${emailAtual.recebidas}); a do e-mail chega primeiro, então o mês corrente cai até fechar.` : ''}` : undefined}
          origem="Fatura que entrou pelo robô do e-mail em algum momento do mês, mesmo que antes tenha sido baixada à mão: o que conta é a UC estar cadastrada na Equatorial para mandar a fatura ao e-mail. Base: UCs do rateio com fatura no mês (Conferência do Faturamento). Vale o mês fechado porque as faturas de um mês chegam até o começo do seguinte."
        />
        <CartaoIndicador
          titulo="Planilha do rateio no prazo"
          valor={prazos ? `${prazos.noPrazo} de ${prazos.meses.length}` : null}
          meta={prazos ? { texto: 'meta: todas no prazo', atingido: prazos.meses.length ? prazos.noPrazo / prazos.meses.length : null } : undefined}
          detalhe={prazos ? prazos.meses.map(m => `${m.mes.slice(0, 3)}: ${m.estado === 'atrasada' ? `${plural(m.diasAtraso ?? 0, 'dia', 'dias')} de atraso` : ROTULO_PRAZO[m.estado]}`).join(' · ') : undefined}
          para="/gestao/rateio-antigo"
          origem="Data em que a planilha subiu no Rateio Antigo, contra o dia 5 do mês seguinte (antes das primeiras faturas). O envio do rateio à Equatorial ainda não é registrado no sistema."
        />
        <CartaoIndicador
          titulo="Geração de hoje"
          valor={gerado ? (gerado.taxaHoje == null ? '—' : pct(gerado.taxaHoje)) : null}
          detalhe={gerado ? `${fmtKwh(gerado.hoje)} até agora, esperado ${fmtKwh(gerado.esperadoHoje)}. ${plural(gerado.abaixoHoje, 'usina está', 'usinas estão')} mais de 15% abaixo.${gerado.taxaOntem != null ? ` Ontem: ${pct(gerado.taxaOntem)}.` : ''}` : undefined}
          para="/geradoras"
          origem="Soma das usinas contra a soma das esperadas pela régua das irmãs (mediana das outras usinas por kWp). Usina parada puxa o número para baixo."
        />
        <CartaoTitularidadeNoPrazo
          assinadas={tit ? tit.assinadas : null}
          protocolos={protocolos.dados}
          inicio={calendario.inicio} fim={calendario.fim} hojeIso={calendario.hojeIso} rotuloMes={calendario.rotulo}
        />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>
        <PainelDetalhe titulo={saldos.dados ? `Saldo das UCs (rateio de ${saldos.dados.mes})` : 'Saldo das UCs'} acao={{ rotulo: 'Saldos das UCs', para: '/gestao/rateio-saldos' }}>
          {saldos.erro ? (
            <p className="text-sm text-red-600 dark:text-red-400">Não foi possível montar o painel de saldos.</p>
          ) : !saldos.dados ? (
            <div className="h-20 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {([
                  ['falta', saldos.dados.indicadores.falta, '/gestao/rateio-saldos'],
                  ['atencao', saldos.dados.indicadores.atencao, '/gestao/rateio-saldos'],
                  ['excesso', saldos.dados.indicadores.excesso, '/gestao/rateio-saldos'],
                ] as const).map(([faixa, n, para]) => (
                  <button key={faixa} type="button" onClick={() => navigate(para)}
                    className="text-left rounded-xl border border-gray-100 dark:border-gray-700 p-3 hover:border-primary/40">
                    <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                      <span className={`w-2 h-2 rounded-full ${FAIXA_META[faixa].ponto}`} />{FAIXA_META[faixa].rotulo}
                    </span>
                    <span className="block text-xl font-bold text-gray-900 dark:text-white mt-1">{n.toLocaleString('pt-BR')}</span>
                  </button>
                ))}
                <button type="button" onClick={() => navigate('/gestao/rateio-saldos?filtro=reincluir')}
                  className="text-left rounded-xl border border-red-100 dark:border-red-900/40 bg-red-50/60 dark:bg-red-900/10 p-3 hover:border-red-300">
                  <span className="flex items-center gap-1.5 text-xs text-red-700 dark:text-red-300"><span className="w-2 h-2 rounded-full bg-red-500" />Voltar ao rateio</span>
                  <span className="block text-xl font-bold text-red-700 dark:text-red-300 mt-1">{saldos.dados.indicadores.reincluir.toLocaleString('pt-BR')}</span>
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Meses de consumo que o saldo de crédito cobre, das {saldos.dados.indicadores.recebendo.toLocaleString('pt-BR')} UCs que recebem energia neste rateio
                ({saldos.dados.indicadores.saudavel.toLocaleString('pt-BR')} na faixa saudável de 1 a 2 meses). "Voltar ao rateio": fora do rateio com saldo para menos de 2 meses, pedir a volta no próximo envio.
                O aviso da Vick de saldo baixo dá um número menor porque conta pelo cadastro da UC e deixa de fora quem ainda espera a primeira injeção.
              </p>
            </>
          )}
        </PainelDetalhe>
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'Rateio Antigo', para: '/gestao/rateio-antigo', icone: Zap },
        { rotulo: 'Saldos das UCs', para: '/gestao/rateio-saldos', icone: BatteryCharging },
        { rotulo: 'Geradoras', para: '/geradoras', icone: Sun },
        { rotulo: 'Conferência do faturamento', para: '/financeiro/conferencia-faturamento', icone: ClipboardCheck },
        { rotulo: 'Recebimento de faturas', para: '/financeiro/recebimento-faturas', icone: FileInput },
      ]} />
    </div>
  );
};

export default TelaBackoffice;
